import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TechStackVisualizer = ({ technologies }) => {
  const [selectedTech, setSelectedTech] = useState(null);
  const [hoveredTech, setHoveredTech] = useState(null);

  const techCategories = {
    'Frontend': ['React', 'Vue.js', 'Angular', 'Next.js', 'TypeScript', 'Tailwind CSS'],
    'Backend': ['Node.js', 'Python', 'Java', 'PHP', 'Ruby', 'Go'],
    'Database': ['MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Firebase'],
    'Cloud': ['AWS', 'Google Cloud', 'Azure', 'Vercel', 'Netlify'],
    'Mobile': ['React Native', 'Flutter', 'Swift', 'Kotlin'],
    'DevOps': ['Docker', 'Kubernetes', 'Jenkins', 'GitHub Actions']
  };

  const getIconForTech = (tech) => {
    const iconMap = {
      'React': 'Atom',
      'Vue.js': 'Triangle',
      'Angular': 'Hexagon',
      'Next.js': 'Zap',
      'TypeScript': 'FileCode',
      'Tailwind CSS': 'Palette',
      'Node.js': 'Server',
      'Python': 'Code',
      'Java': 'Coffee',
      'PHP': 'Globe',
      'Ruby': 'Gem',
      'Go': 'Play',
      'MongoDB': 'Database',
      'PostgreSQL': 'Database',
      'MySQL': 'Database',
      'Redis': 'Layers',
      'Firebase': 'Flame',
      'AWS': 'Cloud',
      'Google Cloud': 'CloudRain',
      'Azure': 'CloudSnow',
      'Vercel': 'Triangle',
      'Netlify': 'Globe',
      'React Native': 'Smartphone',
      'Flutter': 'Smartphone',
      'Swift': 'Smartphone',
      'Kotlin': 'Smartphone',
      'Docker': 'Package',
      'Kubernetes': 'Boxes',
      'Jenkins': 'Settings',
      'GitHub Actions': 'GitBranch'
    };
    return iconMap?.[tech] || 'Code';
  };

  const getPositionForIndex = (index, total, radius = 150) => {
    const angle = (index / total) * 2 * Math.PI;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    return { x, y };
  };

  return (
    <div className="relative w-full h-96 bg-card rounded-2xl overflow-hidden shadow-3d border border-border/50">
      {/* Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, var(--color-accent) 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }} />
      </div>
      {/* Central Hub */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="relative w-80 h-80"
        >
          {/* Center Node */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow-accent z-10">
            <Icon name="Cpu" size={24} className="text-accent-foreground" />
          </div>

          {/* Technology Nodes */}
          {Object.entries(techCategories)?.map(([category, techs], categoryIndex) => (
            <div key={category}>
              {techs?.map((tech, techIndex) => {
                const totalTechs = Object.values(techCategories)?.flat()?.length;
                const globalIndex = Object.values(techCategories)?.slice(0, categoryIndex)?.flat()?.length + techIndex;
                const position = getPositionForIndex(globalIndex, totalTechs, 120);
                
                return (
                  <motion.div
                    key={tech}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    style={{
                      x: position?.x,
                      y: position?.y
                    }}
                    whileHover={{ scale: 1.2, z: 10 }}
                    onMouseEnter={() => setHoveredTech(tech)}
                    onMouseLeave={() => setHoveredTech(null)}
                    onClick={() => setSelectedTech(selectedTech === tech ? null : tech)}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ${
                      selectedTech === tech 
                        ? 'bg-accent text-accent-foreground shadow-accent' 
                        : hoveredTech === tech
                        ? 'bg-accent/20 text-accent border-2 border-accent/50' :'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }`}>
                      <Icon name={getIconForTech(tech)} size={16} />
                    </div>
                    {/* Connection Line to Center */}
                    <div className={`absolute top-1/2 left-1/2 w-px bg-gradient-to-r from-transparent via-accent/30 to-transparent transform -translate-y-1/2 origin-left transition-opacity duration-300 ${
                      hoveredTech === tech || selectedTech === tech ? 'opacity-100' : 'opacity-20'
                    }`} style={{
                      width: Math.sqrt(position?.x * position?.x + position?.y * position?.y),
                      transform: `rotate(${Math.atan2(position?.y, position?.x)}rad) translateY(-50%)`
                    }} />
                    {/* Tech Label */}
                    <AnimatePresence>
                      {(hoveredTech === tech || selectedTech === tech) && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.8, y: 10 }}
                          className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md whitespace-nowrap border border-border/50 shadow-3d"
                        >
                          {tech}
                          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-popover border-l border-t border-border/50 rotate-45" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          ))}
        </motion.div>
      </div>
      {/* Category Legend */}
      <div className="absolute bottom-4 left-4 right-4">
        <div className="flex flex-wrap gap-2 justify-center">
          {Object.keys(techCategories)?.map((category, index) => (
            <div
              key={category}
              className="px-3 py-1 bg-muted/50 text-muted-foreground text-xs rounded-full border border-border/30"
            >
              {category}
            </div>
          ))}
        </div>
      </div>
      {/* Selected Technology Info */}
      <AnimatePresence>
        {selectedTech && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="absolute top-4 right-4 bg-popover text-popover-foreground p-4 rounded-lg border border-border/50 shadow-3d max-w-xs"
          >
            <div className="flex items-center space-x-2 mb-2">
              <Icon name={getIconForTech(selectedTech)} size={16} className="text-accent" />
              <h4 className="font-semibold">{selectedTech}</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              {selectedTech === 'React' && "A JavaScript library for building user interfaces with component-based architecture."}
              {selectedTech === 'Node.js' && "A JavaScript runtime built on Chrome's V8 engine for server-side development."}
              {selectedTech === 'MongoDB' && "A NoSQL document database designed for modern application development."}
              {selectedTech === 'AWS' && "Amazon Web Services - comprehensive cloud computing platform."}
              {!['React', 'Node.js', 'MongoDB', 'AWS']?.includes(selectedTech) && `${selectedTech} is a powerful technology used in modern web development.`}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TechStackVisualizer;